// Search hook with state management and recent searches
// Handles search execution, debouncing, and persistence

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { searchEntries, getSearchSuggestions } from '../utils/search/searchEngine';
import { useJournal } from '../../features/journal/context/JournalContext';
import logger from '../utils/core/logger';


const RECENT_SEARCHES_KEY = 'clarity_recent_searches';
const MAX_RECENT_SEARCHES = 10;

/**
 * Load recent searches from localStorage
 */
function loadRecentSearches() {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (stored) {
      const searches = JSON.parse(stored);
      return Array.isArray(searches) ? searches : [];
    }
  } catch (error) {
    logger.warn('Failed to load recent searches:', error);
  }
  return [];
}

/**
 * Save recent searches to localStorage
 */
function saveRecentSearches(searches) {
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
  } catch (error) {
    logger.warn('Failed to save recent searches:', error);
  }
}

/**
 * useSearch hook
 *
 * Provides search functionality with:
 * - Debounced search
 * - Recent searches persistence
 * - Filter management
 * - Search suggestions
 *
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay (default: 300ms)
 * @param {number} options.minQueryLength - Minimum query length to search (default: 2)
 */
export function useSearch(options = {}) {
  const {
    debounceMs = 300,
    minQueryLength = 2
  } = options;

  const { entries } = useJournal();

  // State
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [recentSearches, setRecentSearches] = useState(loadRecentSearches());

  const debounceTimerRef = useRef(null);

  // Debounce query
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (query.length === 0) {
      setDebouncedQuery('');
      setIsSearching(false);
      return;
    }

    if (query.length < minQueryLength) {
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(query);
      setIsSearching(false);
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query, debounceMs, minQueryLength]);

  // Execute search
  const results = useMemo(() => {
    if (!debouncedQuery || debouncedQuery.length < minQueryLength) {
      return [];
    }

    return searchEntries(entries, debouncedQuery, {
      filters,
      limit: 50,
      includeSnippets: true,
      minScore: 10
    });
  }, [entries, debouncedQuery, filters, minQueryLength]);

  // Get search suggestions
  const suggestions = useMemo(() => {
    if (!query || query.length < 2) return [];
    return getSearchSuggestions(entries, query);
  }, [entries, query]);

  // Add to recent searches
  const addRecentSearch = useCallback((searchQuery) => {
    if (!searchQuery || searchQuery.trim().length === 0) return;

    const trimmed = searchQuery.trim();

    setRecentSearches(prev => {
      // Remove duplicates and add to front
      const filtered = prev.filter(s => s.toLowerCase() !== trimmed.toLowerCase());
      const updated = [trimmed, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      saveRecentSearches(updated);
      return updated;
    });
  }, []);

  // Clear recent searches
  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    saveRecentSearches([]);
  }, []);

  // Remove specific recent search
  const removeRecentSearch = useCallback((searchQuery) => {
    setRecentSearches(prev => {
      const updated = prev.filter(s => s !== searchQuery);
      saveRecentSearches(updated);
      return updated;
    });
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
    setFilters({});
  }, []);

  // Update query
  const updateQuery = useCallback((newQuery) => {
    setQuery(newQuery);
  }, []);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Clear specific filter
  const clearFilter = useCallback((filterKey) => {
    setFilters(prev => {
      const updated = { ...prev };
      delete updated[filterKey];
      return updated;
    });
  }, []);

  // Execute search from recent/suggestion — clear any pending debounce so it
  // doesn't fire later and overwrite the explicitly chosen query.
  const executeSearch = useCallback((searchQuery) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setQuery(searchQuery);
    setDebouncedQuery(searchQuery);
    addRecentSearch(searchQuery);
  }, [addRecentSearch]);

  return {
    // State
    query,
    debouncedQuery,
    filters,
    results,
    suggestions,
    recentSearches,
    isSearching,
    hasResults: results.length > 0,
    hasQuery: debouncedQuery.length >= minQueryLength,

    // Actions
    updateQuery,
    updateFilters,
    clearFilter,
    clearSearch,
    executeSearch,
    addRecentSearch,
    clearRecentSearches,
    removeRecentSearch
  };
}

export default useSearch;
